import type { TCashierTab } from '~/store/cashier';
import { useUiStore } from '~/store/ui';
import SupportChat from '~/utils/services/support-chat.service';

interface IBadgeData {
  value: string | number;
  variant: 'round' | 'square';
}

interface ISidebarLink {
  title?: string;
  icon?: string;
  to?: string;
  action?: () => void | Promise<void>;
  badge?: IBadgeData;
  disabled?: boolean;
  key?: string;
  isActive?: boolean;
  isDivider?: boolean;
}

// const BadgeNewSquare: IBadgeData = { value: 'NEW', variant: 'square' };

export function useSidebarLinks(emit: (evt: 'close') => void) {
  const route = useRoute();
  const deviceType = useDeviceType();
  const gtag = useGtag();

  const chatIsLoading = ref(false);

  const useDesktopSidebar = computed(() => {
    return ['2xl', '3xl'].includes(deviceType.activeBreakpoint.value);
  });

  // const getCategoryBadge = computed(() => {
  //   return (category: EGameCategory) => {
  //     const hasNew = !!stores.games.list.filter((game) => game.isNew).find((game) => game.category === category);
  //     return hasNew ? BadgeNewSquare : undefined;
  //   };
  // });

  const { store: tournamentStore } = useTournamentsLoader();
  const { store: vipStore } = useVipFeature();

  async function handleSignOut() {
    emit('close');
    await stores.user.signOutOfFirebase();
    useCookie('idToken').value = null;
    navigateTo({ name: ERouteName.LOGGED_OUT });
  }

  async function toggleLiveChat() {
    if (chatIsLoading.value) return;

    if (!SupportChat.getIsInitialized()) {
      chatIsLoading.value = true;
      const userId = stores.user.getUserId();
      await SupportChat.init(userId || undefined);
      chatIsLoading.value = false;
    }

    SupportChat.toggle();
  }

  async function handleItemClick(item: Partial<ISidebarLink>) {
    if (typeof item.action === 'function') {
      await item.action();
    } else if (item.to) {
      await navigateTo(item.to);
    }

    if (useDesktopSidebar.value || ['Notifications', 'Live-Chat'].includes(`${item.key}`)) return;
    useUiStore().toggleSidebar(false);
  }

  function openCashier(tabName: TCashierTab) {
    gtag.reportGtagConversion();
    stores.modals.showCashierModal({ tabName });
  }

  const content = computed<ISidebarLink[]>(() => {
    const links: ISidebarLink[] = [
      {
        title: 'Lobby',
        to: '/lobby',
        icon: 'Home',
      },
      {
        title: 'Player Vs Player Games',
        to: '/playervsplayer',
        icon: 'Pvp',
        badge: undefined, // getCategoryBadge.value(EGameCategory.PlayerVsPlayer),
      },
      {
        title: 'SoloPlay Games',
        to: '/soloplay',
        icon: 'SoloPlay',
        badge: undefined, // getCategoryBadge.value(EGameCategory.SoloPlay),
      },
      {
        title: 'Tournaments',
        to: '/bonuses',
        icon: 'Crown',
        badge: undefined,
        disabled: !tournamentStore.value?.isEnabled,
      },
      {
        title: 'VIP Club',
        to: '/vip-club',
        icon: 'Star',
        disabled: !vipStore.value?.isEnabled,
      },
      {
        title: 'Challenge Friends',
        to: '/challenge-friends',
        icon: 'Shield',
      },
      {
        isDivider: true,
      },
      {
        title: 'Notifications',
        icon: 'Bell',
        action: () => useUiStore().toggleNotificationPanel(),
        badge: {
          value: stores.firebase.filteredAlerts.length,
          variant: 'round',
        },
      },
      {
        title: 'My Account',
        to: deviceType.isDesktop.value ? '/account/profile' : '/account',
        icon: 'Account',
      },
      {
        title: 'Refer a Friend',
        to: '/referfriends',
        icon: 'RafHands',
      },
      {
        title: 'Live Chat',
        icon: 'ChatBubble',
        action: toggleLiveChat,
      },
      {
        isDivider: true,
      },
      {
        title: 'Logout',
        icon: 'Logout',
        action: handleSignOut,
      },
    ];

    return links
      .filter((link) => ('disabled' in link ? !link.disabled : true))
      .map((link, index) => ({
        ...link,
        isActive: 'to' in link ? route.path === link.to : false,
        key: 'title' in link ? link.title.split(' ').join('-') : `divider-${index}`,
      })) as ISidebarLink[];
  });

  return { content, emit, handleItemClick, openCashier };
}
